import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

interface AdministratorData {
  id: number;
  status: 'Active' | 'Inactive';
  name: string;
  consultantReference: string;
  consultantName: string;
  consultantSurname: string;
  consultantPhone: string;
  consultantEmail: string;
  taxNumber?: string;
  quotationsEmail?: string;
  claimsEmail?: string;
  instructionsEmail?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  region?: string;
  suburb?: string;
  postalCode?: string;
  country?: string;
}

const AdministratorEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<AdministratorData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAdministrator = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/administrators/${id}`);
        setFormData(res.data.data || res.data); // handle both API formats
      } catch (err) {
        console.error("Error fetching administrator:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAdministrator();
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    if (!formData) return;

    const { name, value } = e.target;

    // Map form field names to the correct property names
    const fieldMapping: Record<string, keyof AdministratorData> = {
      name: 'name',
      consultantReference: 'consultantReference',
      taxNumber: 'taxNumber',
      consultantName: 'consultantName',
      consultantSurname: 'consultantSurname',
      consultantEmail: 'consultantEmail',
      consultantPhone: 'consultantPhone',
      quotationsEmail: 'quotationsEmail',
      claimsEmail: 'claimsEmail',
      instructionsEmail: 'instructionsEmail',
      status: 'status',               // ← add this mapping too
    };

    const fieldName = fieldMapping[name] || name as keyof AdministratorData;

    console.log(`Changing field: ${name} -> ${fieldName} = ${value}`);

    setFormData({
      ...formData,
      [fieldName]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData) {
      console.error("Form data is missing");
      return;
    }
    
    // Use camelCase field names to match the controller
    const payload = {
      name: formData.name,
      consultantReference: formData.consultantReference,
      taxNumber: formData.taxNumber || "",
      consultantName: formData.consultantName,
      consultantSurname: formData.consultantSurname,
      consultantEmail: formData.consultantEmail,
      consultantPhone: formData.consultantPhone,
      quotationsEmail: formData.quotationsEmail || "",
      claimsEmail: formData.claimsEmail || "",
      instructionsEmail: formData.instructionsEmail || "",
      status: formData.status,
    };
    
    console.log("Payload being sent (camelCase):", payload);
    console.log("Payload keys:", Object.keys(payload));
    
    try {
      const response = await axios.put(`${API_URL}/api/administrators/${id}`, payload);
      console.log("Update successful:", response.data);
      navigate(`/insurers/administrators/${id}`);
    } catch (err: unknown) {
      console.error("Error updating administrator:", err);
      
      // Type guard to check if it's an AxiosError
      if (axios.isAxiosError(err)) {
        console.error("Axios error response:", err.response?.data);
        console.error("Axios error status:", err.response?.status);
        console.error("Axios error message:", err.message);
      } else if (err instanceof Error) {
        console.error("Generic error:", err.message);
      } else {
        console.error("Unknown error:", err);
      }
    }
  };

  if (loading) return <p>Loading...</p>;
  if (!formData) return <p>Administrator not found</p>;

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-3xl">
        <h1 className="text-2xl font-bold mb-6">Edit Administrator</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="mt-1 w-full border rounded-lg px-3 py-2"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Consultant Reference</label>
              <input
                type="text"
                name="consultantReference"
                value={formData.consultantReference}
                onChange={handleChange}
                className="mt-1 w-full border rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Tax Number</label>
              <input
                type="text"
                name="taxNumber"
                value={formData.taxNumber || ""}
                onChange={handleChange}
                className="mt-1 w-full border rounded-lg px-3 py-2"
              />
            </div>
          </div>

          {/* Consultant Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Consultant First Name</label>
              <input
                type="text"
                name="consultantName"
                value={formData.consultantName}
                onChange={handleChange}
                className="mt-1 w-full border rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Consultant Last Name</label>
              <input
                type="text"
                name="consultantSurname"
                value={formData.consultantSurname}
                onChange={handleChange}
                className="mt-1 w-full border rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">Status *</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
              className="mt-1 w-full border rounded-lg px-3 py-2 bg-white"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Consultant Email</label>
              <input
                type="email"
                name="consultantEmail"
                value={formData.consultantEmail}
                onChange={handleChange}
                className="mt-1 w-full border rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Consultant Phone</label>
              <input
                type="text"
                name="consultantPhone"
                value={formData.consultantPhone}
                onChange={handleChange}
                className="mt-1 w-full border rounded-lg px-3 py-2"
              />
            </div>
          </div>

          {/* Other Emails */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Quotations Email</label>
              <input
                type="email"
                name="quotationsEmail"
                value={formData.quotationsEmail || ""}
                onChange={handleChange}
                className="mt-1 w-full border rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Claims Email</label>
              <input
                type="email"
                name="claimsEmail"
                value={formData.claimsEmail || ""}
                onChange={handleChange}
                className="mt-1 w-full border rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Instructions Email</label>
              <input
                type="email"
                name="instructionsEmail"
                value={formData.instructionsEmail || ""}
                onChange={handleChange}
                className="mt-1 w-full border rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
                type="button"
                onClick={() => navigate(`/insurers/administrators/${id}`)}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-2 rounded-lg"
            >
                Cancel
            </button>
            <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg"
            >
                Save Changes
            </button>
            </div>

        </form>
      </div>
    </div>
  );
};

export default AdministratorEdit;
